/**
 * ProfessionalReport — Formal regulatory water quality assessment report
 *
 * Conforms to engineering report standards:
 * - Document control table (Report Ref / Issue Date / Revision / Status)
 * - Client and site block with permit reference
 * - All dates in "dd Month yyyy" long format (en-GB)
 * - Sample dates formatted in table
 * - Regulatory limits, compliance matrix
 * - Prepared by / standard certification mark
 */
import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area,
} from 'recharts';
import {
  Download, Building2, CheckCircle2, AlertTriangle,
  ArrowLeft, FileText, MapPin, Hash, Shield, Calendar,
} from 'lucide-react';
import { ALL_STANDARDS, type StandardCode } from '@/types/standards';
import { generateSampleTrendReportPDF } from '@/lib/pdfGenerator';

/* ─── Types ─────────────────────────────────────────────────────────────── */

interface ReportSample {
  name: string;
  date: string;
  params: Record<string, number>;
}

interface Props {
  reportNumber: string;
  reportTitle: string;
  customerName?: string;
  /** Site name for the discharge/sampling point */
  siteName?: string;
  /** Physical address of the site */
  siteAddress?: string;
  /** Discharge permit / EPL / licence reference */
  sitePermitRef?: string;
  /**
   * Regulatory authority with jurisdiction over this site.
   * Derived from site state/country metadata — NOT from user-entered notes,
   * ensuring accuracy even when notes contain errors.
   * e.g. "NSW Environment Protection Authority (NSW EPA)"
   */
  siteJurisdiction?: string;
  /** Document revision (default "0 — Initial Issue") */
  revision?: string;
  samples: ReportSample[];
  createdAt: string | Date;
  onBack?: () => void;
  standard?: string;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

/** Format any ISO date or date string to "dd Month yyyy" (en-GB long) */
function fmtDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return String(iso); }
}

/** Short version "dd Mon yyyy" for chart axis ticks */
function fmtDateShort(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return iso; }
}

function DarkTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-600 bg-slate-900/95 backdrop-blur-sm px-3 py-2 shadow-xl">
      <p className="text-xs font-bold text-slate-300 mb-1">{fmtDate(label)}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="text-xs flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-mono font-bold">
            {typeof p.value === 'number' ? p.value.toLocaleString('en-GB', { maximumFractionDigits: 1 }) : p.value}
          </span>
        </p>
      ))}
    </div>
  );
}

/* ─── Component ──────────────────────────────────────────────────────────── */

export default function ProfessionalReport({
  reportNumber, reportTitle, customerName, siteName, siteAddress, sitePermitRef,
  siteJurisdiction, revision = '0 — Initial Issue',
  samples, createdAt, onBack, standard = 'UK_EA',
}: Props) {
  const sortedSamples = useMemo(() =>
    [...samples].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
  [samples]);

  // Resolve regulatory standard
  const std = useMemo(() => ALL_STANDARDS.find(s => s.code === (standard as StandardCode)) || null, [standard]);
  const stdLimits = std?.limits ?? null;

  const chartData = useMemo(() =>
    sortedSamples.map(s => {
      const p = s.params || {};
      return {
        date: s.date,          // ISO for sort/axis
        label: fmtDateShort(s.date),  // axis label
        name: s.name,
        BOD: p.bod5 ?? p.bod ?? p.BOD ?? 0,
        COD: p.cod ?? p.COD ?? 0,
        TSS: p.tss ?? p.TSS ?? 0,
        pH:  p.ph  ?? p.pH  ?? 0,
        TDS: p.tds ?? p.TDS ?? 0,
        NH3: p.ammoniaN ?? p.nh3 ?? p.NH3 ?? 0,
        OandG: p.oilGrease ?? p.oandg ?? p['O&G'] ?? 0,
        Lead: p.lead ?? p.Pb ?? 0,
        Chromium: p.chromium ?? p.Cr ?? 0,
      };
    }),
  [sortedSamples]);

  const issueDate  = fmtDate(createdAt);
  const issueDateTime = (() => {
    try {
      const d = new Date(createdAt);
      return `${fmtDate(d)} at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} UTC`;
    } catch { return issueDate; }
  })();

  const samplingStart = sortedSamples.length ? fmtDate(sortedSamples[0].date) : '—';
  const samplingEnd   = sortedSamples.length ? fmtDate(sortedSamples[sortedSamples.length - 1].date) : '—';
  const samplingPeriod = samplingStart === samplingEnd
    ? samplingStart
    : `${samplingStart} to ${samplingEnd}`;

  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const bodVals = chartData.map(d => d.BOD).filter(v => v > 0);
    const codVals = chartData.map(d => d.COD).filter(v => v > 0);
    const tssVals = chartData.map(d => d.TSS).filter(v => v > 0);
    const phVals  = chartData.map(d => d.pH).filter(v => v > 0);
    const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    return {
      avgBOD: avg(bodVals), maxBOD: Math.max(...bodVals, 0),
      avgCOD: avg(codVals), maxCOD: Math.max(...codVals, 0),
      avgTSS: avg(tssVals), maxTSS: Math.max(...tssVals, 0),
      avgPH: avg(phVals),   minPH: Math.min(...phVals, 14),
      sampleCount: chartData.length,
    };
  }, [chartData]);

  const handleDownloadPDF = () => {
    generateSampleTrendReportPDF(
      sortedSamples.map(s => ({
        name: s.name,
        date: new Date(s.date),
        params: s.params as Record<string, number>,
      })),
      {
        reportNumber,
        reportTitle,
        customerName,
        siteName,
        siteAddress,
        permitRef: sitePermitRef,
        regulatoryAuthority: siteJurisdiction,
        standard,
        createdAt: new Date(createdAt),
      },
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="rb-card p-12 text-center">
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400">No sample data available for this report.</p>
        {onBack && (
          <button onClick={onBack} className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 mx-auto transition-all">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5 print-report">

      {/* ── Toolbar (hidden on print) ── */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        {onBack && (
          <button onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-all">
            <ArrowLeft className="w-4 h-4" /> Back to Reports
          </button>
        )}
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-rb-active text-white hover:bg-rb-surface transition-all shadow-md border border-rb-brand/30">
            <Download className="w-4 h-4 text-rb-brand" /> Download Regulatory PDF
          </button>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════════
          REPORT HEADER — Document control + client/site identification
          ═════════════════════════════════════════════════════════════════════ */}
      <div className="rb-card border border-slate-800/50 overflow-hidden">
        {/* Letterhead bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-rb-brand/20 bg-rb-brand/5">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-rb-brand" />
            <div>
              <p className="text-xs font-black text-rb-brand uppercase tracking-widest">Reynolds &amp; Bauhm</p>
              <p className="text-[10px] text-slate-500">Water Treatment Engineering</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Water Quality Assessment Report</p>
            {std && <p className="text-[10px] text-slate-600">{std.flag} {std.name}</p>}
          </div>
        </div>

        {/* Report title */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-800/40">
          <h1 className="text-xl font-bold text-white">{reportTitle}</h1>
          <p className="text-sm text-slate-500 mt-1">{std?.report.headerSubtitle}</p>
        </div>

        {/* Document control table */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-700/40 border-b border-slate-800/40">
          {[
            { label: 'Report Reference', value: reportNumber, mono: true, accent: true },
            { label: 'Issue Date', value: issueDate, mono: false, accent: false },
            { label: 'Revision', value: revision, mono: false, accent: false },
            { label: 'Status', value: 'ISSUED', mono: false, accent: false, badge: true },
          ].map((cell, i) => (
            <div key={i} className="px-4 py-3 bg-slate-800/20">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">{cell.label}</p>
              {cell.badge ? (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase tracking-wider">
                  {cell.value}
                </span>
              ) : (
                <p className={`text-sm font-bold ${cell.accent ? 'text-rb-brand font-mono' : 'text-white'}`}>
                  {cell.value}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Client + Site identification */}
        <div className="px-6 py-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {customerName && (
              <div className="flex items-start gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                <div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Client</span>
                  <span className="text-white font-semibold">{customerName}</span>
                </div>
              </div>
            )}
            {siteName && (
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Site / Discharge Point</span>
                  <span className="text-white font-semibold">{siteName}</span>
                </div>
              </div>
            )}
            {siteAddress && (
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                <div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Site Address</span>
                  <span className="text-slate-300">{siteAddress}</span>
                </div>
              </div>
            )}
            {sitePermitRef && (
              <div className="flex items-start gap-2">
                <Hash className="w-3.5 h-3.5 text-rb-brand mt-0.5 shrink-0" />
                <div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Permit / Licence Reference</span>
                  <span className="text-rb-brand font-mono font-bold">{sitePermitRef}</span>
                </div>
              </div>
            )}
            <div className="flex items-start gap-2">
              <Shield className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Regulatory Authority</span>
                <span className="text-white">
                  {siteJurisdiction || (std ? `${std.flag} ${std.name}` : standard)}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Applied Standard</span>
                <span className="text-white">{std ? `${std.flag} ${std.name}` : standard}</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Sampling Period</span>
                <span className="text-white">{samplingPeriod}</span>
              </div>
            </div>
          </div>
          {std && (
            <p className="text-[10px] text-slate-600 mt-2 pt-2 border-t border-slate-800/40">
              {std.report.legalReference}
            </p>
          )}
        </div>

        {/* Prepared by */}
        <div className="px-6 py-3 border-t border-slate-800/40 bg-slate-800/10 flex flex-wrap gap-x-8 gap-y-1 text-[11px] text-slate-500">
          <span>Prepared by: <strong className="text-slate-300">Reynolds &amp; Bauhm Water Treatment Engineering</strong></span>
          <span>Samples: <strong className="text-white">{stats?.sampleCount ?? 0}</strong></span>
          <span>Generated: <strong className="text-slate-300">{issueDateTime}</strong></span>
          {std && <span>Certification: <strong className="text-rb-brand">{std.report.certificationMark}</strong></span>}
        </div>
      </div>

      {/* ═════ KPI CARDS ═════ */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Avg BOD', value: stats.avgBOD.toFixed(0), unit: 'mg/L', color: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/20' },
            { label: 'Avg COD', value: stats.avgCOD.toFixed(0), unit: 'mg/L', color: 'text-rb-brand',   bg: 'bg-rb-brand/10',   border: 'border-rb-brand/20' },
            { label: 'Avg TSS', value: stats.avgTSS.toFixed(0), unit: 'mg/L', color: 'text-slate-300',   bg: 'bg-slate-800/30',   border: 'border-slate-700' },
            { label: 'Avg pH',  value: stats.avgPH.toFixed(2),  unit: '',     color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
            { label: 'Max BOD', value: stats.maxBOD.toFixed(0), unit: 'mg/L', color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20' },
            { label: 'Max COD', value: stats.maxCOD.toFixed(0), unit: 'mg/L', color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20' },
            { label: 'Max TSS', value: stats.maxTSS.toFixed(0), unit: 'mg/L', color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/20' },
            { label: 'Min pH',  value: stats.minPH.toFixed(2),  unit: '',     color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/20' },
          ].map((kpi, i) => (
            <div key={i} className={`p-3 rounded-xl border ${kpi.border} ${kpi.bg} text-center`}>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{kpi.label}</div>
              <div className={`text-lg font-bold ${kpi.color} mt-1`}>{kpi.value}</div>
              {kpi.unit && <div className="text-[10px] text-slate-600">{kpi.unit}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ═════ CHARTS ═════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rb-card p-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">BOD, COD &amp; TSS Trend</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="bodGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#22d3ee" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="codGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="var(--rb-brand)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--rb-brand)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <ReTooltip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xs-)', color: 'var(--rb-text-2)' }} />
                <Area type="monotone" dataKey="BOD" stroke="#22d3ee" fill="url(#bodGrad)"  strokeWidth={2} />
                <Area type="monotone" dataKey="COD" stroke="var(--rb-brand)" fill="url(#codGrad2)" strokeWidth={2} />
                <Line type="monotone" dataKey="TSS" stroke="#64748b" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-text-3)' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rb-card p-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">pH &amp; Nutrients Trend</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="left"  tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <ReTooltip content={<DarkTooltip />} />
                <Legend wrapperStyle={{ fontSize: 'var(--rb-font-xs-)', color: 'var(--rb-text-2)' }} />
                <Bar yAxisId="left"  dataKey="pH"     fill="var(--rb-brand)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="NH3"    fill="var(--rb-active)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="OandG"  fill="#64748b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ═════ DATA TABLE ═════ */}
      <div className="rb-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800/50 bg-slate-800/30 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Sample Data — Analytical Results</h3>
          <span className="text-[10px] text-slate-500">All concentrations in mg/L unless stated</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800/60 border-b border-slate-700">
                {[
                  { h: 'Sample Reference', sub: '' },
                  { h: 'Sample Date', sub: '' },
                  { h: 'BOD₅', sub: 'mg/L' },
                  { h: 'COD', sub: 'mg/L' },
                  { h: 'TSS', sub: 'mg/L' },
                  { h: 'pH', sub: 'units' },
                  { h: 'NH₃-N', sub: 'mg/L' },
                  { h: 'O&G', sub: 'mg/L' },
                  { h: 'Pb', sub: 'mg/L' },
                  { h: 'Cr', sub: 'mg/L' },
                ].map((col, i) => (
                  <th key={i} className="py-2.5 px-3 font-bold text-slate-400 uppercase text-[10px] tracking-wider text-left whitespace-nowrap">
                    {col.h}
                    {col.sub && <span className="block text-[9px] font-normal text-slate-600 normal-case tracking-normal">{col.sub}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="py-2.5 px-3 font-semibold text-white">{row.name}</td>
                  <td className="py-2.5 px-3 text-slate-400 whitespace-nowrap">{fmtDate(row.date)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-cyan-400">{row.BOD > 0 ? row.BOD.toFixed(1) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-rb-brand">{row.COD > 0 ? row.COD.toFixed(1) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TSS > 0 ? row.TSS.toFixed(1) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-white">{row.pH > 0 ? row.pH.toFixed(2) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.NH3 > 0 ? row.NH3.toFixed(2) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.OandG > 0 ? row.OandG.toFixed(2) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.Lead > 0 ? row.Lead.toFixed(3) : '—'}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.Chromium > 0 ? row.Chromium.toFixed(3) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═════ COMPLIANCE SUMMARY ═════ */}
      <div className="rb-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">Compliance Assessment</h3>
          {std && (
            <span className="text-[10px] text-slate-500 font-medium">
              {std.flag} {std.name} — {std.labels.dischargeLimitsTitle}
            </span>
          )}
        </div>

        {/* Limits reference */}
        {stdLimits && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[10px] text-slate-500 mb-4 pb-3 border-b border-slate-800/40">
            <span>COD limit: <strong className="text-slate-300">{stdLimits.cod} mg/L</strong></span>
            <span>BOD₅ limit: <strong className="text-slate-300">{stdLimits.bod5} mg/L</strong></span>
            <span>TSS limit: <strong className="text-slate-300">{stdLimits.tss} mg/L</strong></span>
            <span>pH range: <strong className="text-slate-300">{stdLimits.phMin}–{stdLimits.phMax}</strong></span>
            <span>O&amp;G limit: <strong className="text-slate-300">{stdLimits.oilGrease} mg/L</strong></span>
          </div>
        )}

        {/* Per-sample compliance cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chartData.map((row, i) => {
            const bodLimit = stdLimits?.bod5  ?? 25;
            const codLimit = stdLimits?.cod   ?? 125;
            const tssLimit = stdLimits?.tss   ?? 35;
            const phMin    = stdLimits?.phMin ?? 6.0;
            const phMax    = stdLimits?.phMax ?? 9.0;
            const bodPass  = row.BOD === 0 || row.BOD <= bodLimit;
            const codPass  = row.COD === 0 || row.COD <= codLimit;
            const tssPass  = row.TSS === 0 || row.TSS <= tssLimit;
            const phPass   = row.pH  === 0 || (row.pH >= phMin && row.pH <= phMax);
            const compliant = bodPass && codPass && tssPass && phPass;
            return (
              <div key={i} className={`p-3 rounded-xl border ${compliant ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-xs font-bold text-white">{row.name}</span>
                    <p className="text-[10px] text-slate-500 mt-0.5">{fmtDate(row.date)}</p>
                  </div>
                  <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold border ${compliant ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                    {compliant
                      ? <><CheckCircle2 className="w-3 h-3" /> COMPLIANT</>
                      : <><AlertTriangle className="w-3 h-3" /> NON-COMPLIANT</>}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1 text-center">
                  {[
                    { label: 'BOD₅', value: row.BOD, limit: bodLimit, pass: bodPass,  decimals: 1 },
                    { label: 'COD',  value: row.COD, limit: codLimit, pass: codPass,  decimals: 1 },
                    { label: 'TSS',  value: row.TSS, limit: tssLimit, pass: tssPass,  decimals: 1 },
                    { label: 'pH',   value: row.pH,  limit: phMax,    pass: phPass,   decimals: 2 },
                  ].map((p, pi) => (
                    <div key={pi} className={`p-1.5 rounded-lg ${p.pass ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <div className="text-[10px] text-slate-500 uppercase font-bold">{p.label}</div>
                      <div className={`text-xs font-bold ${p.pass ? 'text-emerald-400' : 'text-red-400'}`}>
                        {p.value > 0 ? p.value.toFixed(p.decimals) : '—'}
                      </div>
                      <div className="text-[10px] text-slate-600">≤{p.limit}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {std && (
          <p className="mt-3 text-[10px] text-slate-600 leading-relaxed border-t border-slate-800/40 pt-2">
            {std.report.complianceStatement}
          </p>
        )}
      </div>

      {/* ═════ FOOTER / CERTIFICATION ═════ */}
      <div className="rb-card border border-slate-800/50 overflow-hidden">
        {/* Certification block */}
        <div className="px-6 py-4 border-b border-slate-800/40 bg-slate-800/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            {[
              { role: 'Prepared by', name: 'Reynolds & Bauhm Engineering', sub: 'Water Treatment Specialist' },
              { role: 'Standard', name: std ? `${std.flag} ${std.shortName}` : standard, sub: std?.report.certificationMark || 'Engineering Reference' },
              { role: 'Report Status', name: 'ISSUED', sub: issueDate, badge: true },
            ].map((cell, i) => (
              <div key={i} className="space-y-1">
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{cell.role}</p>
                {cell.badge ? (
                  <span className="inline-block px-3 py-0.5 rounded-full text-xs font-black bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase">
                    {cell.name}
                  </span>
                ) : (
                  <p className="text-sm font-bold text-white">{cell.name}</p>
                )}
                <p className="text-[10px] text-slate-500">{cell.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Legal footer */}
        <div className="px-6 py-3 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-rb-brand" />
            <span className="font-bold text-white text-sm">Reynolds &amp; Bauhm</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500 text-sm">Water Treatment Engineering</span>
          </div>
          {std && (
            <p className="text-[10px] text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {std.report.footerText}
            </p>
          )}
          <div className="mt-3 pt-2 border-t border-slate-800/40 text-[10px] text-slate-600 font-mono flex flex-wrap justify-center gap-x-4 gap-y-0.5">
            <span>Ref: {reportNumber}</span>
            <span>|</span>
            <span>Issued: {issueDate}</span>
            <span>|</span>
            <span>Samples: {stats?.sampleCount ?? 0}</span>
            <span>|</span>
            <span>Period: {samplingPeriod}</span>
            {sitePermitRef && <><span>|</span><span>Permit: {sitePermitRef}</span></>}
            <span>|</span>
            <span>{std ? `${std.shortName} — ${std.report.certificationMark}` : standard}</span>
            <span>|</span>
            <span className="text-amber-600/70">CONFIDENTIAL — Not for public distribution</span>
          </div>
        </div>
      </div>

    </div>
  );
}
